import React from 'react'
import { Play, Pause, Volume2, Heart, Share2, Calendar, MapPin, ShoppingBag } from 'lucide-react'

interface MusicCreatorProProps {
  data: {
    artistName: string
    artistBio: string
    profileImage: string
    coverImage: string
    tracks: Array<{
      id: string
      title: string
      duration: string
      plays: number
      isPlaying?: boolean
    }>
    tourDates: Array<{
      id: string
      venue: string
      location: string
      date: string
      ticketUrl: string
    }>
    merchandise: Array<{
      id: string
      name: string
      price: string
      image: string
      description: string
    }>
    socialLinks: {
      spotify: string
      appleMusic: string
      youtube: string
      instagram: string
      twitter: string
    }
  }
}

export default function MusicCreatorPro({ data }: MusicCreatorProProps) {
  const [currentTrack, setCurrentTrack] = React.useState<string | null>(null)

  const togglePlay = (trackId: string) => {
    setCurrentTrack(currentTrack === trackId ? null : trackId)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-purple-900 text-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${data.coverImage || '/api/placeholder/1920/1080'})`,
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 text-center max-w-4xl mx-auto px-6">
          {/* Profile Image */}
          <div className="mb-8">
            <img
              src={data.profileImage || '/api/placeholder/300/300'}
              alt={data.artistName}
              className="w-48 h-48 rounded-full mx-auto border-4 border-gold-400 shadow-2xl"
            />
          </div>
          
          {/* Artist Name */}
          <h1 
            className="text-6xl md:text-8xl font-bold mb-6"
            style={{ 
              fontFamily: 'Georgia, serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              background: 'linear-gradient(45deg, #F59E0B, #8B5CF6, #EF4444)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {data.artistName}
          </h1>
          
          {/* Bio */}
          <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            {data.artistBio}
          </p>
          
          {/* Social Links */}
          <div className="flex justify-center gap-6 mb-8">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center hover:bg-gold-500 hover:text-black transition-all duration-300"
              >
                {platform.charAt(0).toUpperCase()}
              </a>
            ))}
          </div>
          
          {/* Play Button */}
          <button className="bg-gold-500 text-black px-8 py-4 rounded-full text-xl font-semibold hover:bg-gold-600 transition-colors flex items-center gap-3 mx-auto">
            <Play className="w-6 h-6" />
            Listen Now
          </button>
        </div>
      </section>

      {/* Music Player Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Latest Releases
          </h2>
          
          <div className="space-y-4">
            {data.tracks.map((track) => (
              <div
                key={track.id}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 hover:bg-white/15 transition-all duration-300"
              >
                <div className="flex items-center gap-6">
                  {/* Play Button */}
                  <button
                    onClick={() => togglePlay(track.id)}
                    className="w-12 h-12 bg-gold-500 text-black rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors"
                  >
                    {currentTrack === track.id ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6" />
                    )}
                  </button>
                  
                  {/* Track Info */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-1">{track.title}</h3>
                    <p className="text-purple-200">{track.plays.toLocaleString()} plays</p>
                  </div>
                  
                  {/* Duration */}
                  <div className="text-purple-200">{track.duration}</div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <button className="p-2 text-white/60 hover:text-red-400 transition-colors">
                      <Heart className="w-5 h-5" />
                    </button>
                    <button className="p-2 text-white/60 hover:text-blue-400 transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tour Dates Section */}
      <section className="py-20 px-6 bg-black/50">
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Tour Dates
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.tourDates.map((date) => (
              <div
                key={date.id}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 hover:bg-white/15 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <Calendar className="w-6 h-6 text-gold-400" />
                  <span className="text-gold-400 font-semibold">{date.date}</span>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">{date.venue}</h3>
                
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-purple-200" />
                  <span className="text-purple-200">{date.location}</span>
                </div>
                
                <a
                  href={date.ticketUrl}
                  className="bg-gold-500 text-black px-6 py-2 rounded-lg hover:bg-gold-600 transition-colors inline-block"
                >
                  Get Tickets
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-12"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Merchandise
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.merchandise.map((item) => (
              <div
                key={item.id}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden hover:bg-white/15 transition-all duration-300 group"
              >
                <div className="aspect-square bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                  <ShoppingBag className="w-16 h-16 text-white/60" />
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{item.name}</h3>
                  <p className="text-purple-200 mb-4">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-gold-400">{item.price}</span>
                    <button className="bg-gold-500 text-black px-4 py-2 rounded-lg hover:bg-gold-600 transition-colors">
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-6 mb-6">
            {Object.entries(data.socialLinks).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-gold-400 transition-colors"
              >
                {platform.charAt(0).toUpperCase() + platform.slice(1)}
              </a>
            ))}
          </div>
          <p className="text-purple-200">
            © 2024 {data.artistName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
